import React, { useState, useEffect } from "react";
import { MdClose, MdSettings, MdEmail } from "react-icons/md";
import "./Navbar.css";
import utilityFunctions from "@store/utilityFunctions";
import apiEndpoints from "@store/apiEndpoints";
import { useToast } from "@components/Toast/ToastContext";

const StaffSettings = ({ handleCloseModal, protectedFetch }) => {
  const { showToast } = useToast();
  const [receiveEmail, setReceiveEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const getUserId = () => utilityFunctions.getUserInfo()?.user?.id || null;

  useEffect(() => {
    const userId = getUserId();
    if (!userId) {
      setError("Unable to load user information.");
      setLoading(false);
      return;
    }

    protectedFetch(
      apiEndpoints.employee.detail(userId),
      "GET",
      null,
      async (res) => {
        if (!res.ok) throw new Error("Failed to load settings");
        return res.json();
      },
    )
      .then((data) => {
        setReceiveEmail(data.receive_email ?? false);
      })
      .catch(() => {
        setError("Failed to load settings. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [protectedFetch]);

  const handleSave = () => {
    const userId = getUserId();
    if (!userId) {
      setError("Unable to identify user.");
      return;
    }

    setSaving(true);
    setError("");

    protectedFetch(
      apiEndpoints.employee.update(userId),
      "PATCH",
      { receive_email: receiveEmail },
      async (res) => {
        if (!res.ok) throw new Error("Failed to save settings");
        return res.json();
      },
    )
      .then(() => {
        showToast("Settings saved successfully.", "success");
        handleCloseModal();
      })
      .catch(() => {
        setError("Failed to save settings. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="staff-settings-modal-container">
      <div className="staff-settings-card">
        <button
          onClick={handleCloseModal}
          className="staff-settings-close-btn"
          aria-label="Close"
        >
          <MdClose size={24} />
        </button>

        <div className="staff-settings-header">
          <div className="staff-settings-header-icon">
            <MdSettings size={32} />
          </div>
          <h2 className="staff-settings-title">Settings</h2>
          <p className="staff-settings-subtitle">
            Manage your notification preferences
          </p>
        </div>

        <div className="staff-settings-body">
          {loading ? (
            <div className="staff-settings-loading">
              <span className="staff-settings-spinner"></span>
              <span>Loading settings...</span>
            </div>
          ) : (
            <div className="staff-settings-section">
              <h3 className="staff-settings-section-title">
                <MdEmail size={18} />
                Email Notifications
              </h3>
              <p className="staff-settings-section-desc">
                Control whether you receive email notifications when you are
                assigned to a ticket.
              </p>
              <label className="staff-settings-toggle-label">
                <div className="staff-settings-toggle-info">
                  <span className="staff-settings-toggle-name">
                    Receive email notifications
                  </span>
                  <span className="staff-settings-toggle-desc">
                    {receiveEmail
                      ? "You will receive email notifications for ticket assignments."
                      : "You will not receive email notifications for ticket assignments."}
                  </span>
                </div>
                <div
                  className={`staff-settings-toggle ${receiveEmail ? "staff-settings-toggle--on" : ""}`}
                  onClick={() => setReceiveEmail((prev) => !prev)}
                  role="switch"
                  aria-checked={receiveEmail}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      setReceiveEmail((prev) => !prev);
                    }
                  }}
                >
                  <div className="staff-settings-toggle-knob" />
                </div>
              </label>
            </div>
          )}

          {error && (
            <div className="staff-settings-alert staff-settings-alert--error">
              {error}
            </div>
          )}
        </div>

        <div className="staff-settings-footer">
          <button
            className="staff-settings-btn staff-settings-btn--secondary"
            onClick={handleCloseModal}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="staff-settings-btn staff-settings-btn--primary"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? (
              <>
                <span className="staff-settings-spinner staff-settings-spinner--sm"></span>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffSettings;
